import React from 'react';
import { Page } from '../types';

interface HeroProps {
  setPage: (page: Page) => void;
}

const Hero: React.FC<HeroProps> = ({ setPage }) => {
  return (
    <section className="relative pt-32 pb-24 lg:pt-56 lg:pb-48 overflow-hidden bg-white dark:bg-slate-950">
      {/* Background Ambience */}
      <div className="absolute top-0 right-0 -z-10 w-full lg:w-[70%] h-full bg-accent/10 dark:bg-primary/5 rounded-bl-[150px] lg:rounded-bl-[400px] animate-fade-in opacity-80"></div>
      <div className="absolute -top-32 -left-32 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px] animate-pulse"></div>
      <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-accent/20 rounded-full blur-[120px] animate-float opacity-40"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-24 lg:gap-36 items-center">
          <div className="relative z-10 text-center lg:text-left">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-primary/5 text-primary dark:bg-white/5 dark:text-accent-300 text-xs sm:text-sm font-bold mb-12 animate-scale-in mx-auto lg:mx-0 border border-primary/10 shadow-sm">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
              </span>
              The Gold Standard of Nigerian Compliance
            </div>
            
            <h1 className="text-6xl sm:text-7xl lg:text-[100px] font-display font-extrabold text-slate-900 dark:text-white leading-[0.95] mb-10 animate-slide-up tracking-tight">
              Empowering <br className="hidden sm:block" />
              Visionary <span className="text-primary italic relative inline-block">
                Founders.
                <span className="absolute bottom-3 left-0 w-full h-4 bg-accent/40 -z-10 rounded-full animate-fade-in stagger-2"></span>
              </span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-slate-600 dark:text-slate-400 mb-14 max-w-xl mx-auto lg:mx-0 leading-relaxed animate-slide-up stagger-1 font-medium">
              Join visionary startups securing their future with CAC automation, strategic tax planning, and premium bookkeeping.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center lg:justify-start animate-slide-up stagger-2">
              <button 
                onClick={() => setPage('booking')} 
                className="btn-premium group inline-flex items-center justify-center px-14 py-6 bg-primary text-white font-bold text-xl rounded-[24px] shadow-premium transition-all"
              >
                Launch Your Empire
                <span className="material-symbols-rounded ml-3 transition-transform group-hover:translate-x-1.5 duration-500">rocket_launch</span>
              </button>
              <button 
                onClick={() => setPage('contact')} 
                className="inline-flex items-center justify-center px-14 py-6 bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-bold text-xl border-2 border-slate-100 dark:border-slate-800 rounded-[24px] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all hover:border-primary/20 hover:shadow-lg"
              >
                Meet the Experts
              </button>
            </div>
          </div>

          <div className="relative mt-20 lg:mt-0 animate-reveal stagger-2">
            <div className="relative rounded-[80px] sm:rounded-[100px] overflow-hidden shadow-[0_50px_100px_-20px_rgba(30,43,109,0.3)] border-[16px] border-white dark:border-slate-900 group">
              <img 
                src="https://res.cloudinary.com/dohuj4mx9/image/upload/v1769740019/fintarck_hero_r1b2cg.jpg" 
                alt="FinTrack Excellence" 
                className="w-full aspect-[4/5] object-cover animate-slow-zoom"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/10 to-transparent opacity-40"></div>
              
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div className="w-24 h-24 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/30 scale-75 group-hover:scale-100 transition-transform duration-700">
                  <span className="material-symbols-rounded text-white text-4xl fill-1">verified</span>
                </div>
              </div>
            </div>
            
            {/* Status Card Overlay - Reimagined without People */}
            <div className="absolute -bottom-16 -left-8 sm:-left-20 bg-white dark:bg-slate-800 p-10 rounded-[48px] shadow-[0_60px_100px_-20px_rgba(0,0,0,0.3)] dark:shadow-[0_60px_100px_-20px_rgba(0,0,0,0.7)] border border-slate-100 dark:border-slate-700 w-[320px] animate-float z-20 hidden sm:block overflow-hidden group/card">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-100 dark:bg-slate-700">
                <div className="h-full w-full bg-gradient-to-r from-primary via-blue-400 to-primary animate-shimmer-fast bg-[length:200%_100%]"></div>
              </div>
              
              <div className="flex items-center gap-6 mb-8">
                <div className="w-16 h-16 bg-slate-50 dark:bg-slate-900 rounded-3xl p-3.5 flex items-center justify-center border border-slate-100 dark:border-slate-700 group-hover/card:rotate-6 transition-transform">
                  <img src="https://bor.cac.gov.ng/static/media/Test.e531c24e977be870325a.png" alt="CAC" className="w-full h-full object-contain" />
                </div>
                <div>
                  <p className="text-[11px] font-black uppercase text-primary/40 tracking-[0.25em] mb-1">CAC Accreditation</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white leading-tight">Verified Agency</p>
                </div>
              </div>
              
              <div className="pt-6 border-t border-slate-100 dark:border-slate-700/50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Filing Accuracy</p>
                  <span className="material-symbols-rounded text-green-500 text-sm">verified</span>
                </div>
                <p className="text-2xl font-black text-primary">100% Guaranteed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;